import {Alarm} from '../../state/alarms/state'

type TipAlarm = Pick<Alarm, 'category' | 'type' | 'subtype' | 'applianceType' | 'taskType'>

export interface ChefsteinTipContent {
  translationKey: string
  fallback: string
}

const tips = {
  'coldAppliance.high': {
    translationKey: 'alarms:modal.chefsteinTip.description.coldAppliance.high',
    fallback:
      'Chefstein tip: Check the appliance temperature, make sure the door is closed, and verify that the appliance is operating normally. Also assess the condition of the food according to your own HACCP plan.'
  },
  'coldAppliance.low': {
    translationKey: 'alarms:modal.chefsteinTip.description.coldAppliance.low',
    fallback:
      'Chefstein tip: Check the appliance temperature and settings. Make sure the products have not frozen and assess their condition according to your own HACCP plan.'
  },
  'dishwasher.high': {
    translationKey: 'alarms:modal.chefsteinTip.description.dishwasher.high',
    fallback:
      'Chefstein tip: Check the wash program and temperature. Verify that the dishwasher is operating normally and, if necessary, assess the cleanliness of the washed dishes according to your own HACCP plan.'
  },
  'dishwasher.low': {
    translationKey: 'alarms:modal.chefsteinTip.description.dishwasher.low',
    fallback:
      'Chefstein tip: Check the wash and rinse temperatures as well as the operation of the dishwasher. If necessary, assess whether the washed dishes meet hygiene requirements according to your own HACCP plan.'
  },
  'cooling.coolingtime': {
    translationKey: 'alarms:modal.chefsteinTip.description.cooling.coolingTimeExceeded',
    fallback:
      "Chefstein tip: Check the product temperature and assess whether the cooling process has been completed as planned. Identify the possible cause of the delay and assess the product's suitability for use according to your own HACCP plan."
  },
  'cooling.high': {
    translationKey: 'alarms:modal.chefsteinTip.description.cooling.high',
    fallback:
      "Chefstein tip: Check the product temperature and the operation of the cooling equipment. Assess the product's suitability for use according to your own HACCP plan."
  },
  'cooling.low': {
    translationKey: 'alarms:modal.chefsteinTip.description.cooling.low',
    fallback:
      'Chefstein tip: Verify that the measurement was performed correctly and that the measuring device is functioning properly. Also confirm that the recorded temperature matches the actual product temperature.'
  },
  'foodTemperature.high': {
    translationKey: 'alarms:modal.chefsteinTip.description.foodTemperature.high',
    fallback:
      'Chefstein tip: Check the measurement result and verify that the measurement was performed correctly. Assess whether the product temperature meets the required limits according to your own HACCP plan.'
  },
  'foodTemperature.low': {
    translationKey: 'alarms:modal.chefsteinTip.description.foodTemperature.low',
    fallback:
      'Chefstein tip: Check the measurement result and the measurement method. Assess whether the product requires further processing before serving or storage according to your own HACCP plan.'
  },
  'hygiene.high': {
    translationKey: 'alarms:modal.chefsteinTip.description.hygiene.high',
    fallback:
      'Chefstein tip: Check the measurement result and assess whether the cleaning was successful. If necessary, repeat the cleaning and perform a new measurement according to your own HACCP plan.'
  }
}

type TipKey = keyof typeof tips
type TipCategory = 'coldAppliance' | 'dishwasher' | 'cooling' | 'foodTemperature' | 'hygiene'
type TipSubtype = 'high' | 'low' | 'coolingtime'

const equipmentTipCategories: Record<string, TipCategory> = {
  dishwasher: 'dishwasher',
  storage: 'coldAppliance',
  freezer: 'coldAppliance'
}

const temperatureTaskTipCategories: Record<string, TipCategory> = {
  dishwasherTemperature: 'dishwasher',
  equipmentTemperature: 'coldAppliance',
  temperature: 'foodTemperature'
}

const tipSubtypes: Record<string, TipSubtype> = {
  HIGH: 'high',
  LOW: 'low',
  COOLINGTIME: 'coolingtime'
}

const buildTipKey = (category: TipCategory | undefined, subtype: TipSubtype): TipKey | null => {
  if (!category) return null

  const tipKey = `${category}.${subtype}`
  return tipKey in tips ? (tipKey as TipKey) : null
}

const getTipKey = (alarm: TipAlarm): TipKey | null => {
  const subtype = alarm.subtype ? tipSubtypes[alarm.subtype] : undefined
  if (!subtype) return null

  switch (`${alarm.category}:${alarm.type}`) {
    case 'equipment:TEMPERATURE':
      return buildTipKey(alarm.applianceType ? equipmentTipCategories[alarm.applianceType] : undefined, subtype)
    case 'task:TEMPERATURE':
      return buildTipKey(alarm.taskType ? temperatureTaskTipCategories[alarm.taskType] : undefined, subtype)
    case 'task:COOLING':
      return buildTipKey('cooling', subtype)
    case 'task:HYGIENE':
      return buildTipKey('hygiene', subtype)
    default:
      return null
  }
}

export const getChefsteinTip = (alarm: TipAlarm): ChefsteinTipContent | null => {
  const tipKey = getTipKey(alarm)
  return tipKey ? tips[tipKey] ?? null : null
}
